import {
  Button,
  Flex,
  Text,
  Grid,
  Card,
  Callout,
  Separator,
} from "@radix-ui/themes";
import { useParams } from "react-router-dom";
import { Copy, AlertTriangle, Info } from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorCard from "@/components/ui/ErrorCard";
import {
  formatCurrency,
  formatDate,
  formatRelativeDate,
  getNextBillingDate,
  isSubscriptionCritical,
  isSubscriptionScheduledCancel,
  providerLabel,
} from "@/types/breeze/AnythinkPay";
import {
  useAnythinkPaySubscription,
  useCancelAnythinkPaySubscription,
  useResumeAnythinkPaySubscription,
} from "@/hooks/useAnythinkPaySubscriptions";
import { useQuery } from "@tanstack/react-query";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { SubscriptionHistoryCard } from "@/components/subscriptions/SubscriptionHistoryCard";
import { Subscription } from "@/types/breeze/AnythinkPay";
import SubscriptionUsers from "@/components/subscription-users/SubscriptionUsers";
import { SubscriptionStatusBadge } from "@/components/subscriptions/SubscriptionStatusBadge";
import { BillingIntervalDisplay } from "@/components/subscriptions/BillingIntervalDisplay";
import { BillingTimeline } from "@/components/subscriptions/BillingTimeline";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import toast from "react-hot-toast";

export default function ViewSubscriptionPage() {
  const { id } = useParams<{ id: string }>();
  const subscriptionId = id ? id : "";
  const confirm = useConfirm();

  // Queries and mutations
  const {
    data: subscription,
    isLoading,
    error,
  } = useAnythinkPaySubscription(subscriptionId);

  const cancelSubscription = useCancelAnythinkPaySubscription();
  const resumeSubscription = useResumeAnythinkPaySubscription();

  const { data: events } = useQuery({
    queryKey: ["subscription-events", subscriptionId],
    queryFn: () =>
      subscriptionId
        ? anythinkPayService.getSubscriptionEvents(subscriptionId)
        : Promise.resolve([]),
    enabled: !!subscriptionId,
  });

  // Resolve a friendly plan name (Apple stores a raw product id like "002") + who the subscription
  // belongs to, so the confirm dialogs read clearly rather than "cancel 002".
  const { data: plans } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: () => anythinkPayService.getSubscriptionPlans(),
  });

  const planLabel = (sub: Subscription): string => {
    if (sub.provider === "apple") {
      return (
        plans?.find((p) => p.apple_product_id === sub.name)?.plan_name ??
        sub.name
      );
    }
    return sub.name;
  };

  const subscriberLabel = (sub: Subscription): string =>
    sub.customer_name || sub.customer_email || "this subscriber";

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    const confirmed = await confirm({
      title: "Cancel Subscription",
      message: `Are you sure you want to cancel "${planLabel(subscription)}" for ${subscriberLabel(subscription)}? The subscription will remain active until the end of the current billing period (${subscription.current_period_ends_at ? formatRelativeDate(subscription.current_period_ends_at) : "end of period"}).`,
      confirmText: "Cancel Subscription",
      cancelText: "Keep Active",
      variant: "warning",
    });

    if (confirmed) {
      cancelSubscription.mutate(subscriptionId);
    }
  };

  const handleResumeSubscription = async () => {
    if (!subscription) return;

    const confirmed = await confirm({
      title: "Resume Subscription",
      message: `Are you sure you want to resume "${planLabel(subscription)}" for ${subscriberLabel(subscription)}? It will continue billing at the end of the current period.`,
      confirmText: "Resume",
      cancelText: "Cancel",
      variant: "info",
    });

    if (confirmed) {
      resumeSubscription.mutate(subscriptionId);
    }
  };

  const handleCopyCheckoutUrl = () => {
    if (subscription?.checkout_url) {
      navigator.clipboard.writeText(subscription.checkout_url);
      toast.success("Checkout URL copied to clipboard!");
    }
  };

  const handleCopySubscriptionId = () => {
    if (subscription?.id) {
      navigator.clipboard.writeText(subscription.id);
      toast.success("Subscription ID copied to clipboard!");
    }
  };

  if (!id) {
    return (
      <PageTemplate
        title="View Subscription"
        description="Subscription ID is required"
      >
        <ErrorCard error="A subscription ID is required to view details." />
      </PageTemplate>
    );
  }

  if (isLoading) {
    return (
      <PageTemplate
        title="View Subscription"
        description="Loading subscription details..."
      >
        <LoadingScreen fullPage={false} />
      </PageTemplate>
    );
  }

  if (error) {
    return (
      <PageTemplate
        title="View Subscription"
        description="Error loading subscription"
      >
        <ErrorCard error={error.message} />
      </PageTemplate>
    );
  }

  if (!subscription) {
    return (
      <PageTemplate
        title="View Subscription"
        description="Subscription not found"
      >
        <ErrorCard error="The subscription you're looking for doesn't exist or you don't have access to it." />
      </PageTemplate>
    );
  }

  const nextBillingDate = getNextBillingDate(subscription);
  const isCritical = isSubscriptionCritical(subscription);
  const isScheduledCancel = isSubscriptionScheduledCancel(subscription);
  const isTrial = subscription.status === "trialing";
  const isIncomplete =
    subscription.status === "incomplete" ||
    subscription.status === "incomplete_expired";

  return (
    <PageTemplate
      title="View Subscription"
      description={`Subscription: ${subscription.name}`}
      rightContent={
        <Flex gap="2">
          {subscription.status === "active" && (
            <Button
              variant="outline"
              color="orange"
              onClick={handleCancelSubscription}
              disabled={cancelSubscription.isPending}
            >
              Cancel Subscription
            </Button>
          )}
          {subscription.status === "canceled" && (
            <Button
              variant="outline"
              color="green"
              onClick={handleResumeSubscription}
              disabled={resumeSubscription.isPending}
            >
              Resume Subscription
            </Button>
          )}
          {isIncomplete && subscription.checkout_url && (
            <Button variant="outline" onClick={handleCopyCheckoutUrl}>
              <Copy size={16} />
              Copy Checkout URL
            </Button>
          )}
        </Flex>
      }
    >
      <Flex direction="column" gap="4">
        {/* Overview Card */}
        <Card>
          <Flex direction="column" gap="4">
            <Flex justify="between" align="start">
              <Flex direction="column" gap="2">
                <Text size="5" weight="bold">
                  {subscription.name}
                </Text>
                {subscription.description && (
                  <Text size="2" color="gray">
                    {subscription.description}
                  </Text>
                )}
              </Flex>
              <SubscriptionStatusBadge
                status={subscription.status}
                size="3"
                showIcon
              />
            </Flex>

            <Separator />

            <Grid columns="3" gap="4">
              <Flex direction="column" gap="2">
                <Text size="1" color="gray">
                  Amount
                </Text>
                <Text size="4" weight="bold">
                  {formatCurrency(subscription.amount, subscription.currency)}
                </Text>
                <BillingIntervalDisplay
                  interval={subscription.billing_interval}
                  intervalCount={subscription.interval_count}
                />
              </Flex>

              {nextBillingDate && (
                <Flex direction="column" gap="2">
                  <Text size="1" color="gray">
                    Next Billing
                  </Text>
                  <Text size="3" weight="bold">
                    {formatRelativeDate(nextBillingDate)}
                  </Text>
                  <Text size="1" color="gray">
                    {formatDate(nextBillingDate)}
                  </Text>
                </Flex>
              )}

              <Flex direction="column" gap="2">
                <Text size="1" color="gray">
                  Payment method
                </Text>
                <Text size="2" weight="medium">
                  {providerLabel(subscription.provider)}
                </Text>
              </Flex>
            </Grid>

            {subscription.last_synced_at && (
              <Flex align="center" gap="2">
                <Text size="1" color="gray">
                  Last synced: {formatRelativeDate(subscription.last_synced_at)}{" "}
                  ({formatDate(subscription.last_synced_at)})
                </Text>
              </Flex>
            )}
          </Flex>
        </Card>

        <SubscriptionHistoryCard events={events ?? []} />

        {/* Warnings and Info */}
        {isCritical && (
          <Callout.Root color="red">
            <Callout.Icon>
              <AlertTriangle size={16} />
            </Callout.Icon>
            <Callout.Text>
              This subscription is in a critical state. Payment has failed and
              the subscription may be canceled soon. Please update the payment
              method.
            </Callout.Text>
          </Callout.Root>
        )}

        {isScheduledCancel && subscription.auto_cancel_at && (
          <Callout.Root color="orange">
            <Callout.Icon>
              <AlertTriangle size={16} />
            </Callout.Icon>
            <Callout.Text>
              This subscription is scheduled to cancel on{" "}
              {formatRelativeDate(subscription.auto_cancel_at)} (
              {formatDate(subscription.auto_cancel_at)}). It will remain active
              until then.
            </Callout.Text>
          </Callout.Root>
        )}

        {isTrial && subscription.trial_ends_at && (
          <Callout.Root color="blue">
            <Callout.Icon>
              <Info size={16} />
            </Callout.Icon>
            <Callout.Text>
              This subscription is in trial period. Trial ends{" "}
              {formatRelativeDate(subscription.trial_ends_at)} (
              {formatDate(subscription.trial_ends_at)}).
            </Callout.Text>
          </Callout.Root>
        )}

        {isIncomplete && (
          <Callout.Root color="blue">
            <Callout.Icon>
              <Info size={16} />
            </Callout.Icon>
            <Callout.Text>
              This subscription setup is incomplete. Share the checkout URL with
              the customer to complete payment setup.
            </Callout.Text>
          </Callout.Root>
        )}

        <Grid columns="2" gap="4">
          {/* Left Column */}
          <Flex direction="column" gap="4">
            {/* Billing Timeline */}
            <Card>
              <Flex direction="column" gap="3">
                <Text size="4" weight="bold">
                  Billing Timeline
                </Text>
                <BillingTimeline subscription={subscription} />
              </Flex>
            </Card>

            {/* Billing Information */}
            <Card>
              <Flex direction="column" gap="3">
                <Text size="4" weight="bold">
                  Billing Information
                </Text>
                <Grid columns="2" gap="3">
                  <Flex direction="column" gap="2">
                    <Text size="1" color="gray">
                      Amount
                    </Text>
                    <Text size="2" weight="medium">
                      {formatCurrency(
                        subscription.amount,
                        subscription.currency,
                      )}
                    </Text>
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="1" color="gray">
                      Billing Interval
                    </Text>
                    <BillingIntervalDisplay
                      interval={subscription.billing_interval}
                      intervalCount={subscription.interval_count}
                    />
                  </Flex>
                  {subscription.trial_period_days && (
                    <Flex direction="column" gap="2">
                      <Text size="1" color="gray">
                        Trial Period
                      </Text>
                      <Text size="2">
                        {subscription.trial_period_days} days
                      </Text>
                    </Flex>
                  )}
                </Grid>
              </Flex>
            </Card>

            {/* Reference Information */}
            {subscription.reference && (
              <Card>
                <Flex direction="column" gap="2">
                  <Text size="4" weight="bold">
                    Reference
                  </Text>
                  <Text size="2">{subscription.reference}</Text>
                </Flex>
              </Card>
            )}
          </Flex>

          {/* Right Column */}
          <Flex direction="column" gap="4">
            {/* Subscription Details */}
            <Card>
              <Flex direction="column" gap="3">
                <Text size="4" weight="bold">
                  Subscription Details
                </Text>
                <Flex direction="column" gap="2">
                  <Flex justify="between">
                    <Text size="2" color="gray">
                      Subscription ID:
                    </Text>
                    <Flex gap="2" align="center">
                      <Text
                        size="1"
                        style={{
                          fontFamily: "monospace",
                          background: "var(--gray-3)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        {subscription.id}
                      </Text>
                      <Button
                        size="1"
                        variant="ghost"
                        onClick={handleCopySubscriptionId}
                        title="Copy Subscription ID"
                      >
                        <Copy size={12} />
                      </Button>
                    </Flex>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2" color="gray">
                      Type:
                    </Text>
                    <Text size="2">{subscription.type}</Text>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2" color="gray">
                      Status:
                    </Text>
                    <SubscriptionStatusBadge status={subscription.status} />
                  </Flex>
                  {subscription.checkout_url && (
                    <Flex justify="between" align="center">
                      <Text size="2" color="gray">
                        Checkout URL:
                      </Text>
                      <Button
                        size="1"
                        variant="outline"
                        onClick={handleCopyCheckoutUrl}
                      >
                        <Copy size={12} />
                        Copy
                      </Button>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Card>

            {/* Important Dates */}
            <Card>
              <Flex direction="column" gap="3">
                <Text size="4" weight="bold">
                  Important Dates
                </Text>
                <Flex direction="column" gap="2">
                  {subscription.current_period_starts_at && (
                    <Flex justify="between">
                      <Text size="2" color="gray">
                        Period Start:
                      </Text>
                      <Text size="2">
                        {formatDate(subscription.current_period_starts_at)}
                      </Text>
                    </Flex>
                  )}
                  {subscription.current_period_ends_at && (
                    <Flex justify="between">
                      <Text size="2" color="gray">
                        Period End:
                      </Text>
                      <Text size="2">
                        {formatDate(subscription.current_period_ends_at)}
                      </Text>
                    </Flex>
                  )}
                  {subscription.trial_ends_at && (
                    <Flex justify="between">
                      <Text size="2" color="gray">
                        Trial End:
                      </Text>
                      <Text size="2">
                        {formatDate(subscription.trial_ends_at)}
                      </Text>
                    </Flex>
                  )}
                  {subscription.auto_cancel_at && (
                    <Flex justify="between">
                      <Text size="2" color="gray">
                        Cancel At:
                      </Text>
                      <Text size="2" color="orange">
                        {formatDate(subscription.auto_cancel_at)}
                      </Text>
                    </Flex>
                  )}
                  {subscription.cancelled_at && (
                    <Flex justify="between">
                      <Text size="2" color="gray">
                        Cancelled:
                      </Text>
                      <Text size="2" color="red">
                        {formatDate(subscription.cancelled_at)}
                      </Text>
                    </Flex>
                  )}
                  <Flex justify="between">
                    <Text size="2" color="gray">
                      Created:
                    </Text>
                    <Text size="2">{formatDate(subscription.created_at)}</Text>
                  </Flex>
                  <Flex justify="between">
                    <Text size="2" color="gray">
                      Updated:
                    </Text>
                    <Text size="2">{formatDate(subscription.updated_at)}</Text>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        </Grid>

        {/* User Access */}
        <Card>
          <SubscriptionUsers subscriptionId={subscriptionId} />
        </Card>

        {/* Payment History Placeholder */}
        <Card>
          <Flex direction="column" gap="2">
            <Text size="4" weight="bold">
              Payment History
            </Text>
            <Text size="2" color="gray">
              Payment history will be available here in a future update.
            </Text>
          </Flex>
        </Card>
      </Flex>
    </PageTemplate>
  );
}
